import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Plus, Pencil, Loader2, Coins, Power, PowerOff, Calculator } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const emptyForm = {
  nombre: "", tipo_vehiculo: "Auto", bloque_inicial_horas: "1", precio_bloque_inicial: "",
  precio_hora_adicional: "", minutos_fraccion: "15", precio_fraccion: "", tolerancia_minutos: "0",
  cobro_estricto_redondeo: true, maximo_diario: "", estatus: true,
};

const FRAC_PRESETS = ["15", "30", "60"];

// Minutos entre dos horas "HH:MM" (envuelve al día siguiente si salida < entrada)
function minutosEntre(entrada, salida) {
  const p = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
  let d = p(salida) - p(entrada);
  if (d < 0) d += 24 * 60;
  return d;
}

// Motor de simulación local (espeja la lógica de negocio: bloque inicial + hora/fracción + tolerancia + tope diario)
function simularCobro(form, totalMin) {
  if (totalMin <= 0) return 0;
  const tol = Number(form.tolerancia_minutos) || 0;
  if (totalMin <= tol) return 0; // dentro de la gracia => sin cobro
  const bloqueHoras = Number(form.bloque_inicial_horas) || 0;
  const precioBloque = Number(form.precio_bloque_inicial) || 0;
  const precioHora = Number(form.precio_hora_adicional) || 0;
  const minFrac = Number(form.minutos_fraccion) || 0;
  const precioFrac = Number(form.precio_fraccion) || 0;
  const estricto = !!form.cobro_estricto_redondeo;
  const maxDiario = form.maximo_diario === "" ? null : Number(form.maximo_diario);

  let total = precioBloque; // cargo mínimo del bloque inicial
  let rem = totalMin - bloqueHoras * 60 - tol; // excedente tras el bloque, aplicando gracia
  if (rem > 0) {
    const fullHoras = Math.floor(rem / 60);
    total += fullHoras * precioHora;
    const resto = rem - fullHoras * 60;
    if (resto > 0) {
      if (minFrac > 0 && precioFrac > 0) {
        total += Math.ceil(resto / minFrac) * precioFrac; // por fracción
      } else if (estricto) {
        total += precioHora; // redondea a hora completa
      } else {
        total += precioHora * (resto / 60); // proporcional
      }
    }
  }
  if (maxDiario != null && maxDiario > 0) total = Math.min(total, maxDiario);
  return Math.round(total * 100) / 100;
}

export default function Tarifas() {
  const [items, setItems] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fracMode, setFracMode] = useState("15"); // 15|30|60|custom|""
  const [matriz, setMatriz] = useState({}); // { id_sucursal: {activo, precio_bloque_excepcion, precio_hora_excepcion} }
  const [confirm, setConfirm] = useState(null);
  const [simEntrada, setSimEntrada] = useState("10:00");
  const [simSalida, setSimSalida] = useState("13:20");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, su] = await Promise.all([
        api.get("/tarifas?incluir_inactivas=true"),
        listSucursales(),
      ]);
      setItems(t.data);
      setSucursales(su.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const emptyMatriz = () => Object.fromEntries(sucursales.map((s) => [s.id_sucursal, { activo: false, precio_bloque_excepcion: "", precio_hora_excepcion: "" }]));

  const openCreate = () => {
    setEditing(null); setForm(emptyForm); setFracMode("15");
    setMatriz(emptyMatriz()); setOpen(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      nombre: t.nombre, tipo_vehiculo: t.tipo_vehiculo || "",
      bloque_inicial_horas: String(t.bloque_inicial_horas), precio_bloque_inicial: String(t.precio_bloque_inicial),
      precio_hora_adicional: String(t.precio_hora_adicional),
      minutos_fraccion: t.minutos_fraccion != null ? String(t.minutos_fraccion) : "",
      precio_fraccion: t.precio_fraccion != null ? String(t.precio_fraccion) : "",
      tolerancia_minutos: String(t.tolerancia_minutos), cobro_estricto_redondeo: t.cobro_estricto_redondeo,
      maximo_diario: t.maximo_diario != null ? String(t.maximo_diario) : "", estatus: t.estatus,
    });
    const fm = t.minutos_fraccion == null ? "" : FRAC_PRESETS.includes(String(t.minutos_fraccion)) ? String(t.minutos_fraccion) : "custom";
    setFracMode(fm);
    const m = emptyMatriz();
    (t.sucursales || []).forEach((c) => {
      m[c.id_sucursal] = {
        activo: c.activo,
        precio_bloque_excepcion: c.precio_bloque_excepcion != null ? String(c.precio_bloque_excepcion) : "",
        precio_hora_excepcion: c.precio_hora_excepcion != null ? String(c.precio_hora_excepcion) : "",
      };
    });
    setMatriz(m); setOpen(true);
  };

  const setRow = (id, patch) => setMatriz((m) => ({ ...m, [id]: { ...m[id], ...patch } }));

  const onFracMode = (v) => {
    setFracMode(v);
    if (v === "custom") return;
    setForm((f) => ({ ...f, minutos_fraccion: v === "" ? "" : v }));
  };

  const totalMin = useMemo(() => minutosEntre(simEntrada, simSalida), [simEntrada, simSalida]);
  const simTotal = useMemo(() => simularCobro(form, totalMin), [form, totalMin]);
  const simHoras = Math.floor(totalMin / 60);
  const simMinutos = totalMin % 60;

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre de la tarifa es obligatorio");
    if (!form.tipo_vehiculo.trim()) return toast.error("El tipo de vehículo es obligatorio");
    setSaving(true);
    try {
      const num2 = (v) => (v === "" || v == null ? null : Number(Number(v).toFixed(2)));
      const sucs = Object.entries(matriz)
        .filter(([, v]) => v.activo || v.precio_bloque_excepcion !== "" || v.precio_hora_excepcion !== "")
        .map(([id, v]) => ({
          id_sucursal: id, activo: !!v.activo,
          precio_bloque_excepcion: num2(v.precio_bloque_excepcion),
          precio_hora_excepcion: num2(v.precio_hora_excepcion),
        }));
      const payload = {
        nombre: form.nombre.trim(),
        tipo_vehiculo: form.tipo_vehiculo.trim(),
        bloque_inicial_horas: parseInt(form.bloque_inicial_horas || 0, 10),
        precio_bloque_inicial: Number(Number(form.precio_bloque_inicial || 0).toFixed(2)),
        precio_hora_adicional: Number(Number(form.precio_hora_adicional || 0).toFixed(2)),
        minutos_fraccion: form.minutos_fraccion === "" ? null : parseInt(form.minutos_fraccion, 10),
        precio_fraccion: num2(form.precio_fraccion),
        tolerancia_minutos: parseInt(form.tolerancia_minutos || 0, 10),
        cobro_estricto_redondeo: !!form.cobro_estricto_redondeo,
        maximo_diario: num2(form.maximo_diario),
        sucursales: sucs,
      };
      if (editing) {
        await api.put(`/tarifas/${editing.id_tarifa}`, { ...payload, estatus: form.estatus });
        toast.success("Tarifa actualizada");
      } else {
        await api.post("/tarifas", payload);
        toast.success("Tarifa creada");
      }
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doToggle = async (t) => {
    try {
      if (t.estatus) await api.delete(`/tarifas/${t.id_tarifa}`);
      else await api.put(`/tarifas/${t.id_tarifa}`, { estatus: true });
      toast.success(t.estatus ? "Tarifa desactivada" : "Tarifa reactivada");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const fld = "h-11 rounded-none border-[#0A0A0A]";
  const lbl = "text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]";

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Motor de tarifas</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Tarifas y precios</h1>
          </div>
          <button onClick={openCreate} data-testid="create-tarifa-btn"
            className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nueva tarifa
          </button>
        </div>

        <div className="bg-white border border-[#e4e4e7]">
          {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          : items.length === 0 ? <div className="p-16 text-center text-[#52525B]"><Coins className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin tarifas registradas.</p></div>
          : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tarifas-table">
              <thead><tr className="border-b border-[#e4e4e7] text-left">
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Tarifa</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Vehículo</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Bloque inicial</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Hora adic.</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Máx. diario</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Estatus</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase text-right">Acciones</th>
              </tr></thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id_tarifa} className="border-b border-[#f0f0f1] hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-5 py-4 font-semibold">{t.nombre}</td>
                    <td className="px-5 py-4">{t.tipo_vehiculo}</td>
                    <td className="px-5 py-4 font-mono">{t.bloque_inicial_horas}h · ${Number(t.precio_bloque_inicial).toFixed(2)}</td>
                    <td className="px-5 py-4 font-mono">${Number(t.precio_hora_adicional).toFixed(2)}</td>
                    <td className="px-5 py-4 font-mono">{t.maximo_diario != null ? `$${Number(t.maximo_diario).toFixed(2)}` : "—"}</td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${t.estatus ? "text-[#047857]" : "text-[#B45309]"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${t.estatus ? "bg-[#10B981]" : "bg-[#F59E0B]"}`} />{t.estatus ? "Activa" : "Inactiva"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right"><div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(t)} data-testid={`edit-tarifa-${t.id_tarifa}`} className="h-8 w-8 inline-flex items-center justify-center border border-[#e4e4e7] hover:border-[#0A0A0A] transition-colors duration-200"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setConfirm(t)} data-testid={`toggle-tarifa-${t.id_tarifa}`}
                        className={`h-8 px-3 inline-flex items-center gap-1.5 border border-[#e4e4e7] text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${t.estatus ? "text-[#B45309] hover:border-[#F59E0B]" : "text-[#047857] hover:border-[#10B981]"}`}>
                        {t.estatus ? <><PowerOff className="h-3.5 w-3.5" /> Desactivar</> : <><Power className="h-3.5 w-3.5" /> Reactivar</>}
                      </button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </motion.div>

      <Sheet open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto border-l-[#0A0A0A]">
          <SheetHeader><SheetTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Editar tarifa" : "Nueva tarifa"}</SheetTitle><SheetDescription>Configura las reglas de cobro y su disponibilidad por sucursal.</SheetDescription></SheetHeader>
          <div className="space-y-5 mt-6">
            {/* Datos generales */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label className={lbl}>Nombre de tarifa</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="tarifa-nombre" className={fld} /></div>
              <div className="space-y-2"><Label className={lbl}>Tipo de vehículo</Label>
                <Input value={form.tipo_vehiculo} onChange={(e) => setForm({ ...form, tipo_vehiculo: e.target.value })} placeholder="Auto, Moto, Camión…" data-testid="tarifa-tipo-vehiculo" className={fld} /></div>
            </div>

            {/* Reglas de cobro */}
            <div className="border border-[#e4e4e7] p-4 space-y-3">
              <Label className={`${lbl} block`}>Reglas de cobro</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className={lbl}>Bloque inicial (horas)</Label>
                  <Input type="number" min="0" value={form.bloque_inicial_horas} onChange={(e) => setForm({ ...form, bloque_inicial_horas: e.target.value })} data-testid="tarifa-bloque-horas" className={fld} /></div>
                <div className="space-y-2"><Label className={lbl}>Precio bloque inicial</Label>
                  <Input type="number" step="0.01" value={form.precio_bloque_inicial} onChange={(e) => setForm({ ...form, precio_bloque_inicial: e.target.value })} data-testid="tarifa-precio-bloque" className={fld} /></div>
                <div className="space-y-2"><Label className={lbl}>Precio hora adicional</Label>
                  <Input type="number" step="0.01" value={form.precio_hora_adicional} onChange={(e) => setForm({ ...form, precio_hora_adicional: e.target.value })} data-testid="tarifa-precio-hora" className={fld} /></div>
                <div className="space-y-2"><Label className={lbl}>Minutos por fracción</Label>
                  <Select value={fracMode} onValueChange={onFracMode}>
                    <SelectTrigger data-testid="tarifa-fraccion-mode" className={fld}><SelectValue placeholder="Sin fracción" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15" data-testid="frac-opt-15">15 min</SelectItem>
                      <SelectItem value="30" data-testid="frac-opt-30">30 min</SelectItem>
                      <SelectItem value="60" data-testid="frac-opt-60">60 min</SelectItem>
                      <SelectItem value="custom" data-testid="frac-opt-custom">Otro…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {fracMode === "custom" && (
                  <div className="space-y-2"><Label className={lbl}>Minutos (personalizado)</Label>
                    <Input type="number" min="1" value={form.minutos_fraccion} onChange={(e) => setForm({ ...form, minutos_fraccion: e.target.value })} data-testid="tarifa-fraccion-custom" className={fld} /></div>
                )}
                <div className="space-y-2"><Label className={lbl}>Precio por fracción</Label>
                  <Input type="number" step="0.01" value={form.precio_fraccion} onChange={(e) => setForm({ ...form, precio_fraccion: e.target.value })} data-testid="tarifa-precio-fraccion" className={fld} /></div>
              </div>
            </div>

            {/* Reglas de holgura */}
            <div className="border border-[#e4e4e7] p-4 space-y-3">
              <Label className={`${lbl} block`}>Reglas de holgura</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label className={lbl}>Tolerancia (minutos)</Label>
                  <Input type="number" min="0" value={form.tolerancia_minutos} onChange={(e) => setForm({ ...form, tolerancia_minutos: e.target.value })} data-testid="tarifa-tolerancia" className={fld} /></div>
                <div className="space-y-2"><Label className={lbl}>Máximo diario (opcional)</Label>
                  <Input type="number" step="0.01" value={form.maximo_diario} onChange={(e) => setForm({ ...form, maximo_diario: e.target.value })} data-testid="tarifa-maximo-diario" className={fld} /></div>
              </div>
              <div className="flex items-center justify-between border border-[#e4e4e7] p-3">
                <div><Label className={lbl}>Cobro estricto (redondeo)</Label><p className="text-[11px] text-[#52525B] mt-1">Cobra el bloque/fracción completa al pasar la tolerancia.</p></div>
                <Switch checked={form.cobro_estricto_redondeo} onCheckedChange={(v) => setForm({ ...form, cobro_estricto_redondeo: v })} data-testid="tarifa-estricto" className="data-[state=checked]:bg-[#0A0A0A]" />
              </div>
              {editing && (
                <div className="flex items-center justify-between border border-[#e4e4e7] p-3">
                  <Label className={lbl}>Activa (estatus)</Label>
                  <Switch checked={form.estatus} onCheckedChange={(v) => setForm({ ...form, estatus: v })} data-testid="tarifa-estatus" className="data-[state=checked]:bg-[#10B981]" />
                </div>
              )}
            </div>

            {/* Simulador de cobro en vivo */}
            <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white p-4 space-y-3" data-testid="tarifa-simulador">
              <div className="flex items-center gap-2"><Calculator className="h-4 w-4 text-[#FFCC00]" /><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#FFCC00]">Simulador de tarifa</Label></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-white/60">Entrada</Label>
                  <Input type="time" value={simEntrada} onChange={(e) => setSimEntrada(e.target.value)} data-testid="sim-entrada" className="h-11 rounded-none border-white/30 bg-white/5 text-white" /></div>
                <div className="space-y-1"><Label className="text-[11px] uppercase tracking-wider text-white/60">Salida</Label>
                  <Input type="time" value={simSalida} onChange={(e) => setSimSalida(e.target.value)} data-testid="sim-salida" className="h-11 rounded-none border-white/30 bg-white/5 text-white" /></div>
              </div>
              <div className="flex items-end justify-between pt-1">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/50">Estancia</p>
                  <p className="font-mono text-sm" data-testid="sim-estancia">{simHoras}h {simMinutos}m ({totalMin} min)</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] uppercase tracking-wider text-white/50">Total estimado</p>
                  <p className="font-display text-3xl font-black text-[#FFCC00]" data-testid="sim-total">${simTotal.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Matriz de disponibilidad por sucursal */}
            <div className="border border-[#e4e4e7] p-4 space-y-3">
              <Label className={`${lbl} block`}>Disponibilidad y excepciones por sucursal</Label>
              {sucursales.length === 0 ? <p className="text-xs text-[#52525B]">No hay sucursales.</p> : (
                <div className="space-y-2" data-testid="tarifa-matriz">
                  {sucursales.map((s) => {
                    const row = matriz[s.id_sucursal] || { activo: false, precio_bloque_excepcion: "", precio_hora_excepcion: "" };
                    return (
                      <div key={s.id_sucursal} className="flex items-center gap-2 bg-[#FAFAFA] border border-[#e4e4e7] px-3 py-2">
                        <Switch checked={row.activo} onCheckedChange={(v) => setRow(s.id_sucursal, { activo: v })} data-testid={`ta-activo-${s.id_sucursal}`} className="data-[state=checked]:bg-[#0A0A0A]" />
                        <span className="text-sm font-medium flex-1 truncate">{s.nombre}</span>
                        <Input type="number" step="0.01" placeholder="$ Bloque" value={row.precio_bloque_excepcion}
                          onChange={(e) => setRow(s.id_sucursal, { precio_bloque_excepcion: e.target.value })}
                          data-testid={`ta-bloque-${s.id_sucursal}`} className="h-9 w-24 rounded-none border-[#0A0A0A] text-sm" />
                        <Input type="number" step="0.01" placeholder="$ Hora" value={row.precio_hora_excepcion}
                          onChange={(e) => setRow(s.id_sucursal, { precio_hora_excepcion: e.target.value })}
                          data-testid={`ta-hora-${s.id_sucursal}`} className="h-9 w-24 rounded-none border-[#0A0A0A] text-sm" />
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-[11px] text-[#52525B]">Los precios de excepción sobreescriben el precio base solo en esa sucursal (opcional).</p>
            </div>

            <button onClick={save} disabled={saving} data-testid="tarifa-save-btn"
              className="w-full h-12 bg-[#0A0A0A] text-white font-bold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#27272A] transition-colors duration-200 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Crear tarifa"}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.estatus ? "¿Desactivar tarifa?" : "¿Reactivar tarifa?"}
        description={confirm?.estatus ? `Se desactivará "${confirm?.nombre}". No se elimina; podrás reactivarla.` : `Se reactivará "${confirm?.nombre}".`}
        confirmLabel={confirm?.estatus ? "Desactivar" : "Reactivar"}
        danger={confirm?.estatus}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) doToggle(c); }}
      />
    </DashboardLayout>
  );
}
